import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { useTelegram } from '@/hooks/useTelegram';
import { useTelegramBackButtonWithConfirm } from '@/hooks/useTelegramBackButton';

interface MeditationTimerProps {
  onComplete: (durationMinutes: number) => void;
  onCancel: () => void;
}

type TimerState = 'setup' | 'running' | 'paused' | 'complete';

const PRESET_DURATIONS = [5, 10, 15, 20, 30];

// Russian pluralization for minutes
function pluralizeMinutes(n: number): string {
  const absN = Math.abs(n);
  const lastTwo = absN % 100;
  const lastOne = absN % 10;

  if (lastTwo >= 11 && lastTwo <= 19) {
    return `${n} минут`;
  }
  if (lastOne === 1) {
    return `${n} минута`;
  }
  if (lastOne >= 2 && lastOne <= 4) {
    return `${n} минуты`;
  }
  return `${n} минут`;
}

export function MeditationTimer({ onComplete, onCancel }: MeditationTimerProps) {
  const [state, setState] = useState<TimerState>('setup');
  const [selectedDuration, setSelectedDuration] = useState(10); // минуты
  const [timeRemaining, setTimeRemaining] = useState(0); // секунды
  const [elapsedTime, setElapsedTime] = useState(0); // секунды
  const [completedMinutes, setCompletedMinutes] = useState(0); // actual completed minutes

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const { hapticFeedback } = useTelegram();

  // Show Telegram BackButton with confirmation when running
  const isRunning = state === 'running' || state === 'paused';
  useTelegramBackButtonWithConfirm(isRunning, onCancel, 'Завершить медитацию?');

  // Wake Lock: keep screen on during meditation
  useEffect(() => {
    if (state === 'running' || state === 'paused') {
      const requestWakeLock = async () => {
        try {
          if ('wakeLock' in navigator) {
            wakeLockRef.current = await navigator.wakeLock.request('screen');
          }
        } catch (_) { /* Wake Lock not supported or denied */ }
      };
      requestWakeLock();
    } else {
      if (wakeLockRef.current) {
        wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    }
    return () => {
      if (wakeLockRef.current) {
        wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    };
  }, [state]);

  // Запуск таймера
  const startTimer = useCallback(() => {
    setTimeRemaining(selectedDuration * 60);
    setElapsedTime(0);
    setState('running');
    hapticFeedback('medium');
  }, [selectedDuration, hapticFeedback]);

  // Пауза
  const pauseTimer = useCallback(() => {
    setState('paused');
    hapticFeedback('light');
  }, [hapticFeedback]);

  // Продолжить
  const resumeTimer = useCallback(() => {
    setState('running');
    hapticFeedback('light');
  }, [hapticFeedback]);

  // Early completion message
  const [earlyMessage, setEarlyMessage] = useState<string | null>(null);

  // Завершить досрочно
  const finishEarly = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    const seconds = elapsedTime;
    const minutes = Math.floor(seconds / 60);

    if (seconds < 60) {
      // < 1 min: Don't count, show message
      setEarlyMessage('Попробуй хотя бы 1 минуту');
      hapticFeedback('warning');
      intervalRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setCompletedMinutes(selectedDuration);
            setState('complete');
            hapticFeedback('success');
            return 0;
          }
          return prev - 1;
        });
        setElapsedTime(prev => prev + 1);
      }, 1000);
      setState('running');
      setTimeout(() => setEarlyMessage(null), 2000);
    } else {
      // 1+ min: Count (backend handles XP based on duration)
      const actualMinutes = Math.max(1, minutes);
      setCompletedMinutes(actualMinutes);
      setState('complete');
      hapticFeedback('success');
    }
  }, [elapsedTime, hapticFeedback, selectedDuration]);

  // Эффект таймера
  useEffect(() => {
    if (state === 'running') {
      intervalRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setCompletedMinutes(selectedDuration);
            setState('complete');
            hapticFeedback('success');
            return 0;
          }
          return prev - 1;
        });

        setElapsedTime(prev => prev + 1);
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [state, selectedDuration, hapticFeedback]);

  // Форматирование времени
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Прогресс (0-100)
  const progress = selectedDuration > 0
    ? ((selectedDuration * 60 - timeRemaining) / (selectedDuration * 60)) * 100
    : 0;

  // Stars background component
  const StarsBackground = () => (
    <div className="stars-container">
      <div className="star star-1" />
      <div className="star star-2" />
      <div className="star star-3" />
      <div className="star star-4" />
      <div className="star star-5" />
      <div className="star star-6" />
      <div className="star star-7" />
      <div className="star star-8" />
      <div className="star star-9" />
      <div className="star star-10" />
      <div className="star star-11" />
      <div className="star star-12" />
      <div className="star star-13" />
      <div className="star star-14" />
      <div className="star star-15" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-950 via-primary-900 to-primary-950 flex flex-col p-6 safe-area-top safe-area-bottom relative overflow-hidden">
      <StarsBackground />

      <AnimatePresence mode="wait">
        {/* Setup Screen */}
        {state === 'setup' && (
          <motion.div
            key="setup"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex-1 flex flex-col relative z-10"
          >
            {/* Header */}
            <div className="flex items-center justify-center mb-8">
              <h1 className="font-serif text-xl text-accent-moon">Тишина</h1>
            </div>

            {/* Moon sphere */}
            <div className="flex-1 flex flex-col items-center justify-center">
              <motion.div
                animate={{
                  scale: [1, 1.03, 1],
                  boxShadow: [
                    '0 0 60px rgba(245, 230, 204, 0.3), 0 0 120px rgba(245, 230, 204, 0.1)',
                    '0 0 80px rgba(245, 230, 204, 0.4), 0 0 160px rgba(245, 230, 204, 0.2)',
                    '0 0 60px rgba(245, 230, 204, 0.3), 0 0 120px rgba(245, 230, 204, 0.1)'
                  ]
                }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                className="moon-sphere w-36 h-36 mb-8"
              >
                <span className="text-4xl">☽</span>
              </motion.div>

              {/* Explanation */}
              <div className="text-center mb-8 max-w-xs">
                <h2 className="font-serif text-xl text-accent-moon mb-3">Войди в тишину</h2>
                <p className="text-primary-300 text-sm mb-3 leading-relaxed">
                  Каждая минута покоя рассеивает тени усталости и наполняет город светом.
                </p>
                <div className="flex justify-center gap-3 text-xs">
                  <span className="text-accent-purple bg-accent-purple/10 px-2 py-1 rounded-full">+3 Внимание</span>
                  <span className="text-accent-rose bg-accent-rose/10 px-2 py-1 rounded-full">+1 Регуляция</span>
                </div>
              </div>

              <h3 className="font-serif text-sm text-primary-200 mb-3">Выбери длительность</h3>

              {/* Duration presets */}
              <div className="flex flex-wrap justify-center gap-2 mb-6">
                {PRESET_DURATIONS.map(duration => (
                  <button
                    key={duration}
                    onClick={() => setSelectedDuration(duration)}
                    className={`px-5 py-2.5 rounded-xl font-medium transition-all ${
                      selectedDuration === duration
                        ? 'bg-gradient-to-br from-accent-warm to-accent-rose text-primary-950'
                        : 'bg-primary-800/50 text-primary-300 hover:bg-primary-800 border border-primary-700/30'
                    }`}
                  >
                    {duration} мин
                  </button>
                ))}
              </div>

              {/* Custom duration slider */}
              <div className="w-full max-w-xs">
                <input
                  type="range"
                  min={1}
                  max={60}
                  value={selectedDuration}
                  onChange={(e) => setSelectedDuration(parseInt(e.target.value))}
                  className="w-full accent-accent-warm"
                />
                <div className="flex justify-between text-xs text-primary-600 mt-1">
                  <span>1 мин</span>
                  <span className="text-accent-moon">{selectedDuration} мин</span>
                  <span>60 мин</span>
                </div>
              </div>
            </div>

            {/* Start button */}
            <Button onClick={startTimer} fullWidth size="lg">
              Войти в тишину
            </Button>
          </motion.div>
        )}

        {/* Running / Paused Screen */}
        {(state === 'running' || state === 'paused') && (
          <motion.div
            key="running"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex-1 flex flex-col relative z-10"
          >
            <div className="flex-1 flex flex-col items-center justify-center">
              {/* Moon sphere with progress */}
              <div className="relative mb-8">
                {/* Outer glow ring */}
                <svg className="w-64 h-64 absolute -inset-2">
                  <defs>
                    <linearGradient id="moonGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#F5E6CC" />
                      <stop offset="50%" stopColor="#E8A87C" />
                      <stop offset="100%" stopColor="#D4A0A0" />
                    </linearGradient>
                  </defs>
                  {/* Background circle */}
                  <circle
                    cx="128"
                    cy="128"
                    r="120"
                    stroke="rgba(155, 142, 196, 0.2)"
                    strokeWidth="3"
                    fill="none"
                  />
                  {/* Progress circle */}
                  <motion.circle
                    cx="128"
                    cy="128"
                    r="120"
                    stroke="url(#moonGradient)"
                    strokeWidth="3"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={2 * Math.PI * 120}
                    strokeDashoffset={2 * Math.PI * 120 * (1 - progress / 100)}
                    initial={{ strokeDashoffset: 2 * Math.PI * 120 }}
                    animate={{ strokeDashoffset: 2 * Math.PI * 120 * (1 - progress / 100) }}
                    transition={{ duration: 0.5 }}
                    style={{ transform: 'rotate(-90deg)', transformOrigin: 'center' }}
                  />
                </svg>

                {/* Moon sphere */}
                <motion.div
                  animate={state === 'running' ? {
                    scale: [1, 1.02, 1],
                    boxShadow: [
                      '0 0 60px rgba(245, 230, 204, 0.3)',
                      '0 0 80px rgba(245, 230, 204, 0.4)',
                      '0 0 60px rgba(245, 230, 204, 0.3)'
                    ]
                  } : {}}
                  transition={{ duration: 4, repeat: Infinity }}
                  className="moon-sphere w-48 h-48 flex flex-col items-center justify-center"
                >
                  <motion.span
                    key={timeRemaining}
                    initial={{ scale: 1.05 }}
                    animate={{ scale: 1 }}
                    className="text-4xl font-serif font-medium text-primary-950"
                  >
                    {formatTime(timeRemaining)}
                  </motion.span>
                  <span className="text-sm text-primary-800/70 mt-1">
                    {state === 'paused' ? 'На паузе' : 'осталось'}
                  </span>
                </motion.div>
              </div>

              {/* Breathing guide */}
              {state === 'running' && !earlyMessage && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center mb-8"
                >
                  <BreathingGuide />
                </motion.div>
              )}

              {/* Early exit message */}
              <AnimatePresence>
                {earlyMessage && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="text-center mb-8 p-3 rounded-xl bg-amber-900/30 border border-amber-800/30"
                  >
                    <p className="text-sm text-amber-300">{earlyMessage}</p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Controls */}
              <div className="flex flex-col items-center gap-3">
                <div className="flex gap-3">
                  {state === 'running' ? (
                    <Button onClick={pauseTimer} variant="secondary" size="lg">
                      ⏸️ Пауза
                    </Button>
                  ) : (
                    <Button onClick={resumeTimer} variant="primary" size="lg">
                      ▶️ Продолжить
                    </Button>
                  )}

                  <Button onClick={finishEarly} variant="ghost" size="lg">
                    Завершить
                  </Button>
                </div>
                <button onClick={onCancel} className="text-primary-500 text-sm">
                  Отмена
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Complete Screen */}
        {state === 'complete' && (
          <motion.div
            key="complete"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex-1 flex flex-col relative z-10"
          >
            <div className="flex-1 flex flex-col items-center justify-center text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', duration: 0.6 }}
                className="moon-sphere w-36 h-36 mb-8"
              >
                <span className="text-5xl">✨</span>
              </motion.div>

              <h2 className="font-serif text-2xl text-accent-moon mb-3">Тишина обретена</h2>
              <p className="text-primary-300 mb-2">
                {pluralizeMinutes(completedMinutes)} покоя
              </p>
              <p className="text-sm text-primary-500 italic mb-8">
                Свет наполняет улицы города...
              </p>

              <Button
                onClick={() => onComplete(completedMinutes)}
                fullWidth
                size="lg"
              >
                В город
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function BreathingGuide() {
  const [phase, setPhase] = useState<'inhale' | 'hold' | 'exhale'>('inhale');

  useEffect(() => {
    const cycle = () => {
      setPhase('inhale');
      setTimeout(() => setPhase('hold'), 4000);
      setTimeout(() => setPhase('exhale'), 6000);
    };

    cycle();
    const interval = setInterval(cycle, 10000);

    return () => clearInterval(interval);
  }, []);

  const labels = {
    inhale: 'Вдох...',
    hold: 'Задержка...',
    exhale: 'Выдох...',
  };

  return (
    <motion.p
      key={phase}
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: [0.5, 1, 0.5] }}
      transition={{ duration: 4, repeat: Infinity }}
      className="text-lg font-serif text-accent-moon"
    >
      {labels[phase]}
    </motion.p>
  );
}
